import { useMemo } from 'react';
import { useResetCountdown } from './useResetCountdown';

/**
 * Vérifie si une date correspond à aujourd'hui
 */
const usedQuotaToday = (timestamp) => {
  if (!timestamp) return false;
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

/**
 * Hook personnalisé pour générer les notifications intelligentes
 * basées sur l'état du compte, de l'abonnement et des jobs.
 * 
 * @param {Object} params
 * @param {number|null} params.credits - Crédits disponibles
 * @param {Object|null} params.userProfile - Profil utilisateur complet
 * @param {Object|null} params.planInfo - Informations du plan (extraites de userProfile.plan)
 * @param {Array} params.jobs - Liste des jobs
 * @param {number} params.upscaleCost - Coût d'un upscale en crédits
 * @param {Function} params.navigate - Fonction de navigation
 * @returns {Array} Liste des notifications à afficher
 */
export const useNotifications = ({
  credits = 0,
  userProfile = null,
  planInfo = null,
  jobs = [],
  upscaleCost = 10,
  navigate,
}) => {
  const { shortFormat: countdownText } = useResetCountdown();
  const notifications = useMemo(() => {
    const list = [];
    const plan = planInfo || userProfile?.plan || {};
    const now = Date.now();
    const ONE_DAY = 24 * 60 * 60 * 1000;

    // ============================================================
    // P0 - CRITIQUE : Paiement échoué
    // ============================================================
    if (plan.paymentFailed) {
      list.push({
        id: 'payment-failed',
        type: 'danger',
        priority: 100,
        title: '⚠️ Paiement échoué',
        message: plan.lastPaymentError 
          ? `Erreur : ${plan.lastPaymentError}. Mettez à jour votre moyen de paiement.`
          : 'Votre dernier paiement a échoué. Mettez à jour votre carte bancaire.',
        actionLabel: 'Mettre à jour',
        onAction: () => navigate('/settings?tab=subscription'),
      });
    }

    // ============================================================
    // LOGIQUE CRÉDITS POUR UTILISATEURS GRATUITS
    // ============================================================
    // Récupérer la date du dernier téléchargement gratuit
    const usage = userProfile?.usage || {};
    const saas = userProfile?.saas_usage || {};
    const tracking = userProfile?.tracking || {};
    
    const lastFreeDownload =
      usage.lastFreeNowatermarkAt ||
      saas.quota_no_watermark_last_use ||
      tracking.last_free_download_at ||
      saas.last_free_download_at ||
      null;
    
    const hasUsedFreeToday = usedQuotaToday(lastFreeDownload);
    const hasEverUsedFreeDownload = lastFreeDownload !== null;
    const isPro = plan.is_active || plan.planType === 'pro' || planInfo?.planType === 'pro';

    if (credits === 0 && !isPro) {
      if (!hasEverUsedFreeDownload) {
        // CAS 1: Nouveau utilisateur - jamais utilisé le téléchargement gratuit
        list.push({
          id: 'welcome-new-user',
          type: 'success',
          priority: 95,
          title: '🎉 Bienvenue sur Muzo !',
          message: 'Tu peux retirer le watermark d\'une vidéo Sora gratuitement chaque jour. Essaie maintenant !',
          actionLabel: 'Essayer gratuitement',
          onAction: () => navigate('/upscale'),
        });
      } else if (hasUsedFreeToday) {
        // CAS 2: A utilisé son téléchargement gratuit aujourd'hui
        list.push({
          id: 'free-used-today',
          type: 'info',
          priority: 95,
          title: '⏰ Reviens bientôt !',
          message: `Ton prochain no watermark gratuit sera disponible dans ${countdownText}`,
          actionLabel: 'Recharger pour plus',
          onAction: () => navigate('/credits'),
        });
      } else {
        // CAS 3: A déjà utilisé le service mais le quota est disponible aujourd'hui
        list.push({
          id: 'free-available',
          type: 'success',
          priority: 95,
          title: '✨ Téléchargement gratuit disponible !',
          message: 'Ton no watermark gratuit du jour t\'attend. Profites-en !',
          actionLabel: 'Utiliser maintenant',
          onAction: () => navigate('/upscale'),
        });
      }
    } else if (credits === 0 && isPro) {
      // Utilisateur Pro sans crédits
      list.push({
        id: 'no-credits-pro',
        type: 'warning',
        priority: 95,
        title: 'Crédits épuisés',
        message: 'Tu peux toujours utiliser le no watermark illimité avec ton abonnement Muzo+.',
        actionLabel: 'Recharger',
        onAction: () => navigate('/credits'),
      });
    }
    // ============================================================
    // P1 - IMPORTANT : Crédits faibles (mais pas 0)
    // ============================================================
    else if (upscaleCost != null && credits < upscaleCost && credits > 0) {
      list.push({
        id: 'low-credits',
        type: 'warning',
        priority: 80,
        title: 'Crédits bientôt épuisés',
        message: `Il te reste ${credits} crédit${credits > 1 ? 's' : ''}. Recharge pour continuer les upscales.`,
        actionLabel: 'Recharger',
        onAction: () => navigate('/credits'),
      });
    }

    // ============================================================
    // P1 - IMPORTANT : Abonnement annulé, expire bientôt
    // ============================================================
    if (plan.cancelAtPeriodEnd && plan.nextBilling && plan.is_active) {
      const expiryDate = new Date(plan.nextBilling);
      const daysLeft = Math.ceil((expiryDate - now) / ONE_DAY);
      
      if (daysLeft <= 7 && daysLeft > 0) {
        list.push({
          id: 'subscription-expiring-soon',
          type: 'warning',
          priority: 85,
          title: `Abonnement expire dans ${daysLeft} jour${daysLeft > 1 ? 's' : ''}`,
          message: `Votre abonnement Muzo+ sera terminé le ${expiryDate.toLocaleDateString('fr-FR')}. Réactivez-le pour conserver vos avantages.`,
          actionLabel: 'Réactiver',
          onAction: () => navigate('/settings?tab=subscription'),
        });
      } else if (daysLeft > 7) {
        list.push({
          id: 'subscription-scheduled-cancel',
          type: 'info',
          priority: 50,
          title: 'Résiliation programmée',
          message: `Votre abonnement s'arrêtera le ${expiryDate.toLocaleDateString('fr-FR')}. Vous conservez vos avantages jusqu'à cette date.`,
          actionLabel: 'Annuler la résiliation',
          onAction: () => navigate('/settings?tab=subscription'),
        });
      }
    }

    // ============================================================
    // P2 - INFO : Souscription récente (bienvenue)
    // ============================================================
    if (plan.subscribedAt && !plan.reactivatedAt) {
      const subscribedDate = new Date(plan.subscribedAt);
      const daysSinceSubscription = (now - subscribedDate) / ONE_DAY;
      
      if (daysSinceSubscription <= 1 && plan.is_active) {
        list.push({
          id: 'welcome-subscriber',
          type: 'success',
          priority: 40,
          title: '🎉 Bienvenue sur Muzo+ !',
          message: 'Votre abonnement est actif. Profitez de 200 crédits mensuels et du no-watermark illimité !',
          actionLabel: 'Commencer',
          onAction: () => navigate('/upscale'),
        });
      }
    }

    // ============================================================
    // P2 - INFO : Réactivation récente
    // ============================================================
    if (plan.reactivatedAt) {
      const reactivatedDate = new Date(plan.reactivatedAt);
      const daysSinceReactivation = (now - reactivatedDate) / ONE_DAY;
      
      if (daysSinceReactivation <= 1 && plan.is_active) {
        list.push({
          id: 'subscription-reactivated',
          type: 'success',
          priority: 45,
          title: '✅ Abonnement réactivé !',
          message: 'Votre abonnement Muzo+ a été réactivé avec succès. Vous conservez tous vos avantages.',
        });
      }
    }

    // ============================================================
    // P2 - INFO : Job échoué récent (dernières 24h)
    // ============================================================
    const failedJob = jobs.find(
      (job) =>
        job.status === 'failed' &&
        job.updated_at &&
        now - new Date(job.updated_at).getTime() < ONE_DAY
    );
    if (failedJob) {
      list.push({
        id: 'job-failed',
        type: 'danger',
        priority: 70,
        title: 'Un traitement a échoué',
        message: 'Un de vos upscales a rencontré une erreur. Vous pouvez le relancer.',
        actionLabel: 'Réessayer',
        onAction: () => navigate(`/upscale?retry=${failedJob.job_id}`),
      });
    }

    // ============================================================
    // P3 - INFO : Job en cours
    // ============================================================
    const pendingJob = jobs.find(
      (job) => job.status === 'processing' || job.status === 'upscale_in_progress'
    );
    if (pendingJob) {
      list.push({
        id: 'job-pending',
        type: 'info',
        priority: 30,
        title: 'Traitement en cours',
        message: 'Un upscale est en train d\'être traité. Ça ne devrait pas tarder !',
      });
    }

    // ============================================================
    // P3 - INFO : Renouvellement imminent (dans les 3 jours)
    // ============================================================
    if (plan.is_active && !plan.cancelAtPeriodEnd && plan.nextBilling) {
      const renewalDate = new Date(plan.nextBilling);
      const daysUntilRenewal = Math.ceil((renewalDate - now) / ONE_DAY);
      
      if (daysUntilRenewal <= 3 && daysUntilRenewal > 0) {
        list.push({
          id: 'renewal-soon',
          type: 'info',
          priority: 20,
          title: 'Renouvellement proche',
          message: `Votre abonnement se renouvellera automatiquement le ${renewalDate.toLocaleDateString('fr-FR')}.`,
        });
      }
    }

    // Trier par priorité (plus haute = plus important = en premier)
    return list.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }, [credits, userProfile, planInfo, jobs, upscaleCost, navigate, countdownText]);

  return notifications;
};

export default useNotifications;
